import type { StrokePattern } from "@refinement-canvas/domain";

/** 保存値欠落や内部の不正値を、後方互換の実線へ正規化する。 */
export function resolveStrokePattern(value: unknown): StrokePattern {
  return value === "dotted" ? "dotted" : "solid";
}

/** 点線の短い描画区間と間隔。丸い line cap と組み合わせて丸点列として表示する。 */
export function strokeDashArray(
  lineWidth: number,
  pattern: StrokePattern | undefined
): readonly number[] {
  if (resolveStrokePattern(pattern) === "solid") return [];
  const width = effectiveLineWidth(lineWidth);
  return [Math.max(0.01, width * 0.01), width * 2];
}

/** 点線 1 周期の長さ。 */
export function strokeDashPeriod(lineWidth: number): number {
  const dash = strokeDashArray(lineWidth, "dotted");
  return (dash[0] ?? 0) + (dash[1] ?? 0);
}

/** Canvas2D の dash 状態を必ず上書きし、別オブジェクトへの漏れを防ぐ。 */
export function applyCanvasStrokePattern(
  ctx: CanvasRenderingContext2D,
  lineWidth: number,
  pattern: StrokePattern | undefined,
  distanceBefore = 0
): void {
  const resolved = resolveStrokePattern(pattern);
  // jsdom や一部の軽量 Canvas 実装は dash API を省略するため、利用可能な場合だけ適用する。
  // ブラウザ標準 Canvas では常に存在し、実線時の空配列指定で以前の点線状態を確実に消去する。
  if (typeof ctx.setLineDash === "function") {
    ctx.setLineDash([...strokeDashArray(lineWidth, resolved)]);
  }
  ctx.lineDashOffset =
    resolved === "dotted" ? -(Math.max(0, distanceBefore) % strokeDashPeriod(lineWidth)) : 0;
}

/** SVG の中心線 path。1 点だけの場合は move のみとなるため、呼び出し側で円を併記する。 */
export function buildStrokeCenterlinePath(
  points: readonly { readonly x: number; readonly y: number }[]
): string {
  return points
    .map((point, index) => `${index === 0 ? "M" : "L"}${point.x},${point.y}`)
    .join(" ");
}

/** 中心点列の累積長。 */
export function strokeCenterlineLength(
  points: readonly { readonly x: number; readonly y: number }[]
): number {
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    if (previous && current) total += Math.hypot(current.x - previous.x, current.y - previous.y);
  }
  return total;
}

/** 1 点・同一点列・最初の可視 dash より短い点線を始点円で補うか。 */
export function needsDottedStrokeStartFallback(
  points: readonly { readonly x: number; readonly y: number }[],
  lineWidth: number,
  pattern: StrokePattern | undefined
): boolean {
  if (points.length === 0 || resolveStrokePattern(pattern) !== "dotted") return false;
  const dashLength = strokeDashArray(lineWidth, "dotted")[0] ?? 0.01;
  return strokeCenterlineLength(points) < dashLength;
}

/** Canvas2D へ極短点線の始点円を描く。描画した場合は true。 */
export function fillDottedStrokeStartFallback(
  ctx: CanvasRenderingContext2D,
  points: readonly { readonly x: number; readonly y: number }[],
  lineWidth: number,
  pattern: StrokePattern | undefined,
  color: string,
  mapPoint: (point: { readonly x: number; readonly y: number }) => {
    readonly x: number;
    readonly y: number;
  } = (point) => point
): boolean {
  if (!needsDottedStrokeStartFallback(points, lineWidth, pattern)) return false;
  const first = points[0];
  if (!first) return false;
  const mapped = mapPoint(first);
  ctx.beginPath();
  ctx.arc(mapped.x, mapped.y, effectiveLineWidth(lineWidth) / 2, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  return true;
}

function effectiveLineWidth(lineWidth: number): number {
  return Number.isFinite(lineWidth) && lineWidth > 0 ? lineWidth : 0.01;
}
